import { useEffect, useRef } from 'react';
import PricingSection from '../components/sections/PricingSection';
import FaqSection     from '../components/sections/FaqSection';
import CtaSection     from '../components/sections/CtaSection';
import SEO from '../components/SEO';
import './PricingPage.css';

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6366F1' }}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function PricingPage() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    const PARTICLE_COUNT = 55;
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * W; this.y = Math.random() * H;
        this.r = Math.random() * 1.8 + 0.4;
        this.dx = (Math.random() - 0.5) * 0.25; this.dy = (Math.random() - 0.5) * 0.25;
        this.o = Math.random() * 0.5 + 0.15;
        this.hue = Math.random() > 0.6 ? 245 : 270;
      }
      update() { this.x += this.dx; this.y += this.dy; if (this.x < 0 || this.x > W || this.y < 0 || this.y > H) this.reset(); }
      draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${this.hue}, 80%, 55%, ${this.o})`; ctx.fill(); }
    }
    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());
    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y;
        const d = Math.sqrt(dx*dx + dy*dy);
        if (d < 100) { ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y); ctx.strokeStyle = `rgba(99,102,241,${0.06*(1-d/100)})`; ctx.lineWidth = 0.5; ctx.stroke(); }
      }
    };
    const animate = () => { ctx.clearRect(0,0,W,H); particles.forEach(p=>{p.update();p.draw();}); drawLines(); animRef.current = requestAnimationFrame(animate); };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div className="pricing-page">
      <SEO
        title="Pricing — Get a Custom WhatsApp Automation Quote on WhatsApp"
        description="Zepliq pricing is tailored to your channels and monthly conversation volume. Message us on WhatsApp for a custom quote — no setup fees, no contracts, cancel anytime."
        keywords="WhatsApp automation pricing, WhatsApp chatbot cost, Zepliq pricing, WhatsApp automation quote, custom chatbot pricing, WhatsApp CRM pricing"
        canonical="/pricing"
      />

      {/* ── HERO ── */}
      <section className="pricing-hero">
        <canvas ref={canvasRef} className="pricing-hero__canvas" aria-hidden="true" />
        <div className="pricing-hero__blob pricing-hero__blob--1" aria-hidden="true" />
        <div className="pricing-hero__blob pricing-hero__blob--2" aria-hidden="true" />
        <div className="container pricing-hero__inner">
          <span className="badge badge-brand">Simple Pricing</span>
          <h1 className="display-xl pricing-hero__title">
            Pricing that fits<br />
            <span className="gradient-text">your business.</span>
          </h1>
          <p className="text-lg pricing-hero__sub">
            No hidden fees. No contracts. Cancel anytime.
            Message us on WhatsApp and we'll quote you in minutes.
          </p>
          <div className="pricing-hero__trust">
            {['Quote in minutes', 'Setup in 5 minutes', 'No setup fees', 'Cancel anytime'].map(t => (
              <span key={t} className="pricing-hero__trust-item">
                <Check /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHATSAPP QUOTE CTA ── */}
      <PricingSection />

      <FaqSection />
      <CtaSection />
    </div>
  );
}
